"use client";

import { useState, useEffect } from "react";
import { useUser } from "@clerk/nextjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface NameChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function NameChangeModal({ isOpen, onClose }: NameChangeModalProps) {
  const { user } = useUser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 모달이 열릴 때 현재 사용자 정보로 초기화 — Clerk 외부 인증 상태 동기화
  useEffect(() => {
    if (isOpen && user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
    }
  }, [isOpen, user]);

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const updateData: { firstName?: string; lastName?: string } = {};
      
      // 빈 문자열이 아닌 경우에만 업데이트
      if (firstName.trim()) {
        updateData.firstName = firstName.trim();
      }
      if (lastName.trim()) {
        updateData.lastName = lastName.trim();
      }
      
      // 변경사항이 있는 경우에만 업데이트
      if (Object.keys(updateData).length > 0) {
        await user.update(updateData);
      }
      
      onClose();
    } catch (error) {
      console.error("이름 변경 실패:", error);
      alert("이름 변경에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // 원래 값으로 되돌리기
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
    }
    onClose();
  };

  const generateRandomName = () => {
    const adjectives = [
      "예쁜", "섹시한", "똑똑한", "영리한", "더러운", "강인한", 
      "달콤한", "신비한", "용감한", "부드러운", "차가운", "따뜻한",
      "빠른", "느린", "큰", "작은", "밝은", "어두운", "신기한", "특별한",
      "귀여운", "멋진", "화려한", "우아한", "활발한", "조용한", "시원한", "뜨거운",
      "신선한", "오래된", "재미있는", "슬픈", "행복한", "화난", "놀란", "무서운",
      "용기있는", "겁쟁이", "친절한", "못된", "착한", "나쁜", "좋은", "훌륭한",
      "멋있는", "못생긴", "아름다운", "못난", "깔끔한", "지저분한", "새로운", "낡은",
      "젊은", "늙은", "건강한", "아픈", "튼튼한", "약한", "강한", "힘센"
    ];
    
    const potatoTypes = [
      "감자", "왕감자", "알감자", "햇감자", "흙감자", 
      "돼지감자", "수미감자", "고구마감자", "새감자", "옛감자"
    ];
    
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomPotato = potatoTypes[Math.floor(Math.random() * potatoTypes.length)];
    
    // 약간의 지연을 두어 브라우저 유효성 검사 회피
    setTimeout(() => {
      setFirstName(randomAdjective);
      setLastName(randomPotato);
    }, 10);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          이름 변경
        </h2>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                이름 (선택)
              </Label>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="이름을 입력하세요"
                className="mt-1"
                autoComplete="off"
              />
            </div>
            
            <div>
              <Label htmlFor="lastName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                성 (선택)
              </Label>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="성을 입력하세요"
                className="mt-1"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={generateRandomName}
            disabled={isLoading}
            className="text-sm"
          >
            🎲 랜덤 이름 짓기
          </Button>
          
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isLoading}
              className="bg-[#706E6E] hover:bg-[#5A5858]"
            >
              {isLoading ? "저장 중..." : "저장"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
